import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { FacilitiesList } from "@/components/rent/facilities-list"
import { FacilityCard } from "@/components/rent/facility-card"
import { FacilityMap } from "@/components/rent/facility-map"
import type { FacilityWithUnitTypes } from "@/types/warehouse"

type WarehouseStepProps = {
  isMobile: boolean
  facilities: FacilityWithUnitTypes[]
  isFacilitiesLoading?: boolean
  selectedFacilityCode: string | null
  selectedFacility?: FacilityWithUnitTypes
  onFacilitySelect: (code: string | null) => void
  footer?: React.ReactNode
}

function FacilityMapWithCard({
  facilities,
  isFacilitiesLoading,
  selectedFacilityCode,
  selectedFacility,
  onFacilitySelect,
}: Pick<
  WarehouseStepProps,
  | "facilities"
  | "isFacilitiesLoading"
  | "selectedFacilityCode"
  | "selectedFacility"
  | "onFacilitySelect"
>) {
  return (
    <>
      <FacilityMap
        facilities={facilities.map((item) => item.facility)}
        isLoading={isFacilitiesLoading}
        selectedFacilityCode={selectedFacilityCode}
        onFacilitySelect={onFacilitySelect}
      />
      <FacilityCard
        facility={selectedFacility?.facility}
        active={!!selectedFacilityCode}
        onClose={() => onFacilitySelect(null)}
      />
    </>
  )
}

export function WarehouseStep({
  isMobile,
  facilities,
  isFacilitiesLoading,
  selectedFacilityCode,
  selectedFacility,
  onFacilitySelect,
  footer,
}: WarehouseStepProps) {
  const [warehouseView, setWarehouseView] = useState<"map" | "list">("map")

  if (isMobile) {
    return (
      <div className="flex h-full min-h-0 flex-col rounded-[8px] bg-card p-2 pt-6">
        <Tabs
          value={warehouseView}
          onValueChange={(value) => setWarehouseView(value as "map" | "list")}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <TabsList
            variant="segment"
            className="grid h-auto w-full shrink-0 grid-cols-2 self-start rounded-lg bg-background p-1"
          >
            <TabsTrigger
              value="map"
              className="!text-[12px] !leading-[24px] data-active:!text-brand"
            >
              Карта со складами
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="!text-[12px] !leading-[24px] data-active:!text-brand"
            >
              Список складов
            </TabsTrigger>
          </TabsList>
          <TabsContent value="map" className="min-h-0 flex-1">
            <div className="relative h-full min-h-0">
              <FacilityMapWithCard
                facilities={facilities}
                isFacilitiesLoading={isFacilitiesLoading}
                selectedFacilityCode={selectedFacilityCode}
                selectedFacility={selectedFacility}
                onFacilitySelect={onFacilitySelect}
              />
            </div>
          </TabsContent>
          <TabsContent value="list" className="min-h-0 flex-1 overflow-y-auto">
            <FacilitiesList
              facilities={facilities}
              isLoading={isFacilitiesLoading}
              value={selectedFacilityCode}
              onValueChange={onFacilitySelect}
            />
          </TabsContent>
        </Tabs>
      </div>
    )
  }

  return (
    <div className="flex h-full gap-2">
      <div className="relative min-h-[480px] grow flex-[58%] rounded-[8px] bg-card">
        <FacilityMapWithCard
          facilities={facilities}
          isFacilitiesLoading={isFacilitiesLoading}
          selectedFacilityCode={selectedFacilityCode}
          selectedFacility={selectedFacility}
          onFacilitySelect={onFacilitySelect}
        />
      </div>
      <div className="relative grow flex-[42%] rounded-[8px] bg-card p-8">
        <FacilitiesList
          facilities={facilities}
          isLoading={isFacilitiesLoading}
          value={selectedFacilityCode}
          onValueChange={onFacilitySelect}
        />
        {footer}
      </div>
    </div>
  )
}
